import {
  daysInRange,
  eachDayISO,
  eachMonthKey,
  eachWeekStartISO,
  monthRange,
  shiftMonth,
  weekStartISO,
  type DateRange,
} from '@core/dates';

import type { Category, Expense } from './types';

/** Same numbers the mobile app computes in SQL, over the in-memory synced data. */

export const inRange = (e: Expense, r: DateRange) => e.expenseDate >= r.from && e.expenseDate <= r.to;

export type Summary = {
  totalMinor: number;
  count: number;
  avgPerDayMinor: number;
  largest: Expense | null;
};

export function summarize(expenses: Expense[], range: DateRange): Summary {
  const rows = expenses.filter((e) => inRange(e, range));
  const totalMinor = rows.reduce((s, e) => s + e.amountMinor, 0);
  const largest = rows.reduce<Expense | null>((m, e) => (!m || e.amountMinor > m.amountMinor ? e : m), null);
  return { totalMinor, count: rows.length, avgPerDayMinor: Math.round(totalMinor / daysInRange(range)), largest };
}

export type Bucket = 'day' | 'week' | 'month';
export type Point = { key: string; totalMinor: number };

export function bucketFor(range: DateRange): Bucket {
  const days = daysInRange(range);
  return days <= 31 ? 'day' : days <= 120 ? 'week' : 'month';
}

export function trend(expenses: Expense[], range: DateRange, bucket: Bucket): Point[] {
  const keyOf = (d: string) => (bucket === 'day' ? d : bucket === 'week' ? weekStartISO(d) : d.slice(0, 7));
  const totals = new Map<string, number>();
  for (const e of expenses) {
    if (!inRange(e, range)) continue;
    const k = keyOf(e.expenseDate);
    totals.set(k, (totals.get(k) ?? 0) + e.amountMinor);
  }
  const keys = bucket === 'day' ? eachDayISO(range) : bucket === 'week' ? eachWeekStartISO(range) : eachMonthKey(range);
  return keys.map((key) => ({ key, totalMinor: totals.get(key) ?? 0 }));
}

export type CategoryTotal = { category: Category; totalMinor: number; count: number };

export function categoryTotals(expenses: Expense[], categories: Map<string, Category>, range: DateRange): CategoryTotal[] {
  const byId = new Map<string, CategoryTotal>();
  for (const e of expenses) {
    if (!inRange(e, range)) continue;
    const category = categories.get(e.categoryId);
    if (!category) continue;
    const t = byId.get(e.categoryId) ?? { category, totalMinor: 0, count: 0 };
    t.totalMinor += e.amountMinor;
    t.count += 1;
    byId.set(e.categoryId, t);
  }
  return [...byId.values()].sort((a, b) => b.totalMinor - a.totalMinor);
}

export function trailingMonths(range: DateRange, months = 6): DateRange {
  const end = range.to.slice(0, 7);
  return { from: monthRange(shiftMonth(end, -(months - 1))).from, to: monthRange(end).to };
}

/** Change vs the same number of days at the start of the previous month (fair for partial months). */
export function monthOverMonth(expenses: Expense[], month: string, throughDay: number | null): number | null {
  const cur = monthRange(month);
  const prev = monthRange(shiftMonth(month, -1));
  const prevDays = Number(prev.to.slice(8));
  const span = throughDay ? Math.min(throughDay, prevDays) : prevDays;
  const curRange = throughDay ? { from: cur.from, to: `${month}-${String(throughDay).padStart(2, '0')}` } : cur;
  const prevRange = { from: prev.from, to: `${prev.from.slice(0, 7)}-${String(span).padStart(2, '0')}` };
  const a = summarize(expenses, curRange).totalMinor;
  const b = summarize(expenses, prevRange).totalMinor;
  return a > 0 && b > 0 ? Math.round(((a - b) / b) * 100) : null;
}
